// ConversationItem.js
import React, { useState } from 'react';
import {
    Flex,
    Box,
    Text,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuGroup,
    MenuDivider,
    IconButton,
    Tooltip,  // Add this import
} from '@chakra-ui/react';
import { ChevronDownIcon, DeleteIcon, EditIcon, ArrowForwardIcon } from '@chakra-ui/icons';
import ConfirmationModal from './ConfirmationModal';
import RenameModal from './RenameModal';

const ConversationItem = ({
    conversation,
    onSelect,
    onDelete,
    onRename,
    onMove,
    workspaces = [],
    currentWorkspaceId,
    isActive,
    onClose,
}) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);

    // Destinations are the user's OTHER workspaces. `workspaces` comes from the
    // server and is already membership-scoped, so this list can only ever offer
    // somewhere the move is actually permitted -- the server re-checks anyway.
    const moveTargets = workspaces.filter((w) => w.id !== currentWorkspaceId);

    // A temporary (unsaved) conversation has no server-side rows to re-point.
    const canMove = Boolean(onMove) && !conversation.isTemporary && moveTargets.length > 0;

    const handleRename = (newName) => {
        if (newName && newName.trim() !== "") {
            onRename(conversation.id, newName.trim());
        }
        setShowRenameModal(false);
    };

    const handleDelete = () => {
        onDelete(conversation.id);
        setShowDeleteModal(false);
    };

    // Display name with unsaved indicator
    const displayName = conversation.isTemporary
        ? `${conversation.name} (Unsaved)`
        : conversation.name;

    return (
        <>
            <Flex
                align="center"
                borderRadius="md"
                px={2}
                py={1}
                mx={1}
                bg={isActive ? 'chat.sidebarActive' : 'transparent'}
                _hover={{ bg: isActive ? 'chat.sidebarActive' : 'chat.sidebarHover' }}
                cursor="pointer"
                onClick={() => {
                    onSelect(conversation.id);
                    if (onClose) onClose(); // ← Close drawer on mobile
                }}
                transition="background-color 0.2s"
            >
                <Box flex="1" minW={0} overflow="hidden" mr={1}>
                    {/* Wrap the Text with Tooltip */}
                    <Tooltip
                        label={displayName}
                        placement="right"
                        hasArrow
                        bg="gray.700"
                        color="white"
                        fontSize="sm"
                        px={2}
                        py={1}
                        borderRadius="md"
                        openDelay={500}
                        closeDelay={200}
                        // Only show tooltip if the text is actually truncated
                        isDisabled={displayName.length <= 30} // Adjust threshold as needed
                    >
                        <Text
                            fontSize="sm"
                            fontWeight={isActive ? "600" : "400"}
                            isTruncated
                            color={isActive ? "brand.600" : "text.primary"}
                            lineHeight="1.3"
                        >
                            {displayName}
                        </Text>
                    </Tooltip>
                </Box>
                
                {/* Menu button - rest remains the same */}
                <Box flexShrink={0}>
                    <Menu>
                        <MenuButton
                            as={IconButton}
                            icon={<ChevronDownIcon />}
                            variant="ghost"
                            size="sm"
                            opacity={isActive ? 1 : 0.6}
                            _hover={{ opacity: 1, bg: 'chat.sidebarHover' }}
                            _focus={{ opacity: 1 }}
                            transition="opacity 0.2s"
                            aria-label="Conversation actions"
                            onClick={e => e.stopPropagation()}
                            color="text.secondary"
                            minW="auto"
                            h={6}
                            w={6}
                        />
                        <MenuList bg="white" borderColor="border.primary" shadow="lg">
                            <MenuItem
                                icon={<EditIcon />}
                                onClick={e => {
                                    e.stopPropagation();
                                    setShowRenameModal(true);
                                }}
                                color="text.primary"
                                _hover={{ bg: 'gray.50' }}
                                fontSize="sm"
                            >
                                Rename
                            </MenuItem>
                            {canMove && (
                                <>
                                    <MenuDivider />
                                    <MenuGroup
                                        title="Move to workspace"
                                        fontSize="xs"
                                        color="text.secondary"
                                    >
                                        {moveTargets.map((workspace) => (
                                            <MenuItem
                                                key={workspace.id}
                                                icon={<ArrowForwardIcon />}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    onMove(conversation.id, workspace.id);
                                                }}
                                                color="text.primary"
                                                _hover={{ bg: 'gray.50' }}
                                                fontSize="sm"
                                            >
                                                {workspace.name}
                                            </MenuItem>
                                        ))}
                                    </MenuGroup>
                                    <MenuDivider />
                                </>
                            )}
                            {!conversation.isTemporary && (
                                <MenuItem
                                    icon={<DeleteIcon />}
                                    onClick={e => {
                                        e.stopPropagation();
                                        setShowDeleteModal(true);
                                    }}
                                    color="red.600"
                                    _hover={{ bg: 'red.50' }}
                                    fontSize="sm"
                                >
                                    Delete
                                </MenuItem>
                            )}
                        </MenuList>
                    </Menu>
                </Box>
            </Flex>

            {/* Modals remain the same */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Conversation"
                message="Are you sure you want to delete this conversation? This action cannot be undone."
                confirmButtonText="Delete"
                confirmButtonColorScheme="red"
            />


            <RenameModal
                isOpen={showRenameModal}
                onClose={() => setShowRenameModal(false)}
                onRename={handleRename}
                currentName={conversation.name}
            />
        </>
    );
};

export default ConversationItem;